import React, { useEffect, useMemo, useState } from 'react'
import { useI18n } from '../../../i18n'
import { Button } from '../../ui/Button'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'

export type PlatformEvidenceCard = {
  sourceType: string
  platformSource?: string
  label?: string
  description?: string
  artifacts?: string[]
  status?: string
  verified?: boolean
  verificationMethod?: string
  section?: 'recommended' | 'other'
  alreadyLinked?: boolean
}

type Props = {
  recommended: PlatformEvidenceCard[]
  other: PlatformEvidenceCard[]
  loading?: boolean
  error?: string | null
  linkingSource?: string | null
  /** Multi-select save (preferred). */
  onSaveSelected?: (sourceTypes: string[]) => void | Promise<void>
  /** Legacy single-link callback (still supported). */
  onLink?: (sourceType: string) => void | Promise<void>
  onClose: () => void
}

function sourceKey(card: PlatformEvidenceCard): string {
  return String(card.sourceType || card.platformSource || '').trim()
}

export function PlatformEvidencePicker({
  recommended,
  other,
  loading,
  error,
  linkingSource,
  onSaveSelected,
  onLink,
  onClose,
}: Props) {
  const { t } = useI18n()
  const all = useMemo(() => [...recommended, ...other], [recommended, other])
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [query, setQuery] = useState('')
  const saving = Boolean(linkingSource)

  useEffect(() => {
    const initial = new Set<string>()
    for (const card of all) {
      if (card.alreadyLinked) {
        const key = sourceKey(card)
        if (key) initial.add(key)
      }
    }
    setSelected(initial)
  }, [all])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return all
    return all.filter((c) => {
      const hay = `${c.label || ''} ${sourceKey(c)} ${c.description || ''}`.toLowerCase()
      return hay.includes(q)
    })
  }, [all, query])

  const grouped = useMemo(() => {
    const map = new Map<string, PlatformEvidenceCard[]>()
    for (const card of filtered) {
      const group = card.section === 'recommended' ? 'recommended' : 'other'
      if (!map.has(group)) map.set(group, [])
      map.get(group)!.push(card)
    }
    return map
  }, [filtered])

  const toggle = (key: string, locked: boolean) => {
    if (locked || saving) return
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const handleSave = async () => {
    const keys = [...selected]
    if (onSaveSelected) {
      await onSaveSelected(keys)
      return
    }
    if (onLink) {
      for (const key of keys) {
        await onLink(key)
      }
    }
  }

  return (
    <div
      className={`${listStyles.evidenceBindingSection} mt-2 rounded-md border border-slate-200 bg-white p-3 shadow-sm`}
      data-testid="platform-controls-picker"
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className={listStyles.pickerSectionTitle}>
          {t('complianceEvidence.platformControlsTitle', 'Link platform controls')}
        </p>
        <Button type="button" size="small" color="outline" onClick={onClose} disabled={saving}>
          {t('common.close', 'Close')}
        </Button>
      </div>

      <p className={`${listStyles.pickerRowMeta} mb-2`}>
        {t(
          'complianceEvidence.platformControlsHint',
          'Select DefendSphere mechanisms that support this requirement. Linking does not mark the requirement compliant by itself.',
        )}
      </p>

      <input
        type="search"
        className="mb-3 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t('complianceEvidence.platformSearch', 'Search platform controls…')}
        aria-label={t('complianceEvidence.platformSearch', 'Search platform controls…')}
      />

      {error ? <p className={`${listStyles.formError} mb-2`}>{error}</p> : null}
      {loading ? <p className={listStyles.pickerRowMeta}>{t('common.loading', 'Loading…')}</p> : null}

      {!loading && filtered.length === 0 ? (
        <p className={listStyles.pickerRowMeta}>
          {t('complianceEvidence.platformEmpty', 'No platform controls available.')}
        </p>
      ) : null}

      {(['recommended', 'other'] as const).map((group) => {
        const rows = grouped.get(group) || []
        if (!rows.length) return null
        return (
          <section key={group} className="mb-3">
            <p className={listStyles.pickerSectionTitle}>
              {group === 'recommended'
                ? t('complianceEvidence.platformRecommended', 'Recommended for this control')
                : t('complianceEvidence.platformOther', 'Other platform controls')}
            </p>
            <ul className={`${listStyles.pickerList} mt-2`}>
              {rows.map((card) => {
                const key = sourceKey(card)
                const checked = selected.has(key)
                return (
                  <li key={key} className={listStyles.pickerRow}>
                    <label className="flex min-w-0 flex-1 cursor-pointer items-start gap-2">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={checked}
                        disabled={saving}
                        onChange={() => toggle(key, false)}
                      />
                      <span className="min-w-0">
                        <span className={listStyles.pickerRowTitle}>{card.label || key}</span>
                        {card.description ? (
                          <span className={`${listStyles.pickerRowMeta} mt-1 block`}>{card.description}</span>
                        ) : null}
                        {card.alreadyLinked ? (
                          <span className="mt-1 inline-block rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-800">
                            {t('complianceEvidence.platformAlreadyLinked', 'Previously linked')}
                          </span>
                        ) : null}
                      </span>
                    </label>
                  </li>
                )
              })}
            </ul>
          </section>
        )
      })}

      <div className="mt-3 flex flex-wrap justify-end gap-2">
        <Button type="button" size="small" color="outline" onClick={onClose} disabled={saving}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button type="button" size="small" color="primary" onClick={() => void handleSave()} disabled={saving || loading}>
          {saving
            ? t('common.saving', 'Saving…')
            : t('complianceEvidence.savePlatformControls', 'Save platform controls')}
        </Button>
      </div>
    </div>
  )
}
